/**
 * useVendors — loads the vendor list once for selection UIs.
 *
 * Used by the adhoc line-item card so staff can name the supplying vendor on
 * a purchase requisition. Fetch happens once on mount; the returned array
 * reference is stable so consumers can safely pass it through `memo`.
 */

import { useEffect, useState } from 'react';
import { listVendors } from '../services/vendorService';
import type { Vendor } from '../../../types';

export interface UseVendors {
  vendors: Vendor[];
  loading: boolean;
  error: string | null;
}

export function useVendors(): UseVendors {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listVendors()
      .then((v) => {
        if (!cancelled) setVendors(v);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load vendors.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { vendors, loading, error };
}
